export interface VisualViewportRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const RECT_EPSILON = 1;
const KEYBOARD_SIZE_DELTA = 80;
const MIN_USABLE_HEIGHT = 160;
const MIN_USABLE_HEIGHT_RATIO = 0.2;
const MAX_SETTLE_TIME_MS = 400;

const readViewportRect = (): VisualViewportRect => {
  const viewport = window.visualViewport;
  return {
    top: viewport?.offsetTop ?? 0,
    left: viewport?.offsetLeft ?? 0,
    width: viewport?.width ?? window.innerWidth,
    height: viewport?.height ?? window.innerHeight,
  };
};

const rectsMatch = (a: VisualViewportRect, b: VisualViewportRect): boolean =>
  Math.abs(a.top - b.top) < RECT_EPSILON &&
  Math.abs(a.left - b.left) < RECT_EPSILON &&
  Math.abs(a.width - b.width) < RECT_EPSILON &&
  Math.abs(a.height - b.height) < RECT_EPSILON;

const hasFocusedTextControl = (): boolean => {
  const active = document.activeElement;
  return active instanceof HTMLTextAreaElement ||
    active instanceof HTMLInputElement ||
    active instanceof HTMLElement && active.isContentEditable;
};

/**
 * Safari can publish a keyboard-sized viewport with the old top offset for a
 * few frames. Applying that mixed snapshot makes a bottom-anchored control
 * jump towards the top of the page. The reverse can happen as the keyboard is
 * dismissed. Keep the last coherent rectangle until the offset catches up.
 */
const isCoherentViewportRect = (
  candidate: VisualViewportRect,
  accepted: VisualViewportRect,
  settleStartedAt: number,
  allowUnpannedKeyboardResize: boolean,
): boolean => {
  const now = performance.now();
  // Never let a browser quirk leave the viewport observer permanently frozen.
  if (now - settleStartedAt >= MAX_SETTLE_TIME_MS) return true;

  const minimumHeight = Math.max(
    MIN_USABLE_HEIGHT,
    window.innerHeight * MIN_USABLE_HEIGHT_RATIO,
  );
  if (candidate.height < minimumHeight) return false;

  if (!hasFocusedTextControl()) return true;

  const heightDelta = candidate.height - accepted.height;
  const topIsUnchanged = Math.abs(candidate.top - accepted.top) < RECT_EPSILON;

  // Keyboard opening: height has collapsed but Safari has not published the
  // focus-induced visual-viewport pan yet.
  if (
    heightDelta < -KEYBOARD_SIZE_DELTA &&
    topIsUnchanged &&
    !allowUnpannedKeyboardResize
  ) {
    return false;
  }

  // Keyboard closing: height has expanded while the old panned offset remains.
  if (
    heightDelta > KEYBOARD_SIZE_DELTA &&
    accepted.top > RECT_EPSILON &&
    topIsUnchanged
  ) {
    return false;
  }

  return true;
};

export interface StableVisualViewportOptions {
  /**
   * Allows a focused control to intentionally open the keyboard without the
   * usual iOS page pan, for example after focus({ preventScroll: true }).
   */
  allowUnpannedKeyboardResize?: () => boolean;
}

/**
 * Observe a coherent visual viewport rectangle without exposing transient
 * keyboard-animation values. Updates are exposed as transient samples for
 * visual compensation, but the full rectangle is committed only after it is
 * stable across consecutive frames and passes keyboard validation.
 */
export const observeStableVisualViewport = (
  onChange: (rect: VisualViewportRect) => void,
  onTransientChange?: (candidate: VisualViewportRect, accepted: VisualViewportRect) => void,
  options: StableVisualViewportOptions = {},
): (() => void) => {
  const viewport = window.visualViewport;
  let accepted = readViewportRect();
  let previousSample: VisualViewportRect | null = null;
  let settleStartedAt: number | null = null;
  let frame: number | null = null;
  let disposed = false;

  onChange(accepted);

  const sampleUntilStable = () => {
    frame = null;
    if (disposed) return;

    const candidate = readViewportRect();
    const samplesAreStable = previousSample !== null && rectsMatch(previousSample, candidate);
    onTransientChange?.(candidate, accepted);
    previousSample = candidate;

    if (
      samplesAreStable &&
      isCoherentViewportRect(
        candidate,
        accepted,
        settleStartedAt ?? performance.now(),
        options.allowUnpannedKeyboardResize?.() ?? false,
      )
    ) {
      if (!rectsMatch(candidate, accepted)) {
        accepted = candidate;
        onChange(candidate);
      }
      previousSample = null;
      settleStartedAt = null;
      return;
    }

    frame = window.requestAnimationFrame(sampleUntilStable);
  };

  const scheduleUpdate = () => {
    if (settleStartedAt === null) settleStartedAt = performance.now();
    previousSample = null;
    if (frame !== null) window.cancelAnimationFrame(frame);
    // The first frame gets out of the viewport event; the next invocation
    // provides the second, comparable sample before anything is committed.
    frame = window.requestAnimationFrame(sampleUntilStable);
  };

  viewport?.addEventListener('resize', scheduleUpdate);
  viewport?.addEventListener('scroll', scheduleUpdate);
  window.addEventListener('resize', scheduleUpdate);
  window.addEventListener('scroll', scheduleUpdate);

  return () => {
    disposed = true;
    viewport?.removeEventListener('resize', scheduleUpdate);
    viewport?.removeEventListener('scroll', scheduleUpdate);
    window.removeEventListener('resize', scheduleUpdate);
    window.removeEventListener('scroll', scheduleUpdate);
    if (frame !== null) window.cancelAnimationFrame(frame);
  };
};
